import React, { useCallback, useState } from "react";
import { Dimensions, ImageBackground, Platform, StyleSheet } from "react-native";
import { Box, Button, Image, Text, Typography } from "components/ui";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import Carousel from "react-native-reanimated-carousel";
import { Carousel as WebCarousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { LinearGradient } from "expo-linear-gradient";
import { PageButton } from "components/page/parts/PageButton";
import { PageContainer } from "components/page/parts/PageContainer";
import Container from "./Container";

interface SliderProps {
	children: React.ReactNode;
	autoPlay?: boolean;
	autoPlayInterval?: number;
	showIndicators?: boolean;
	showArrows?: boolean;
	infiniteLoop?: boolean;
	arrowPosition?: "bottom" | "center" | "custom";
	arrowCustomPosition?: { bottom?: number; left?: number };
}

const InternalBannerSlider = ({ children, autoPlay = true, autoPlayInterval = 5000, showIndicators = true, showArrows = true, infiniteLoop = true, arrowPosition = "bottom", arrowCustomPosition }: SliderProps) => {
	const width = Dimensions.get("window").width;
	const childrenArray = React.Children.toArray(children);
	const carouselRef: any = React.useRef(null);
	const isMobile = useIsMobile();
	const [currentIndex, setCurrentIndex] = useState(0);

	const hasChildren = childrenArray.length > 0;

	// Web navigation handlers
	const handlePrev = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.decrement();
		}
	}, []);

	const handleNext = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.increment();
		}
	}, []);

	// Native navigation handlers
	const handleNativePrev = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollTo({ count: -1, animated: true });
		}
	}, []);

	const handleNativeNext = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollTo({ count: 1, animated: true });
		}
	}, []);

	const handleSlideChange = useCallback((index: number) => {
		setCurrentIndex(index);
	}, []);

	const getArrowStyles = () => {
		if (arrowPosition === "center") {
			return {
				position: "absolute" as const,
				bottom: "50%",
				transform: [{ translateY: 18 }],
				width: "100%",
				justifyContent: "space-between" as const,
				paddingHorizontal: 16,
				flexDirection: "row" as const,
				zIndex: 10,
			};
		} else if (arrowPosition === "custom" && arrowCustomPosition) {
			return {
				position: "absolute" as const,
				bottom: arrowCustomPosition.bottom || 20,
				left: arrowCustomPosition.left || "8%",
				flexDirection: "row" as const,
				gap: 8,
				zIndex: 10,
			};
		}
		// Default bottom position
		return {
			position: "absolute" as const,
			bottom: isMobile ? 40 : 70,
			left: isMobile ? 24 : 47,
			flexDirection: "row" as const,
			gap: 8,
			zIndex: 10,
		};
	};

	if (isMobile && Platform.OS !== "web") {
		if (!hasChildren) return null;

		return (
			<Box flex={1} position="relative" width="$full">
				<Carousel
					loop={infiniteLoop}
					ref={carouselRef}
					width={width}
					style={{ width: "100%" }}
					height={578}
					panGestureHandlerProps={{ activeOffsetX: [-10, 10] }}
					autoPlay={autoPlay}
					autoPlayInterval={autoPlayInterval}
					data={childrenArray}
					scrollAnimationDuration={1000}
					onSnapToItem={handleSlideChange}
					renderItem={({ item }) => <Box height="$full">{item}</Box>}
				/>

				{showArrows && (
					// @ts-ignore
					<Box position="relative">
						<Box {...getArrowStyles()}>
							<Button backgroundColor="white" borderWidth={1} w={36} h={36} padding={0} borderRadius={12} onPress={handleNativePrev}>
								<ChevronLeft color="black" />
							</Button>
							<Button backgroundColor="white" borderWidth={1} padding={0} w={36} h={36} borderRadius={12} onPress={handleNativeNext}>
								<ChevronRight color="black" />
							</Button>
						</Box>
					</Box>
				)}

				{showIndicators && childrenArray.length > 1 && (
					<Box position="absolute" bottom={40} width="100%" flexDirection="row" justifyContent="center" gap={8}>
						{childrenArray.map((_, index) => (
							<Box key={`indicator-${index}`} width={8} height={8} borderRadius={4} backgroundColor={currentIndex === index ? "black" : "gray"} />
						))}
					</Box>
				)}
			</Box>
		);
	}

	if (!hasChildren) return null;

	return (
		<Box marginVertical={12}>
			<Box position="relative">
				<WebCarousel
					ref={carouselRef}
					showIndicators={showIndicators}
					showStatus={false}
					className="carousel_custom"
					showThumbs={false}
					showArrows={false}
					// @ts-ignore
					showIndicators={false}
					autoPlay={autoPlay}
					interval={autoPlayInterval}
					infiniteLoop={infiniteLoop}
					onChange={handleSlideChange}
				>
					{childrenArray.map((child, key) => (
						<Box key={key}>{child}</Box>
					))}
				</WebCarousel>

				{showArrows && childrenArray.length > 1 && (
					// @ts-ignore
					<Box {...getArrowStyles()}>
						<Button onPress={handlePrev} backgroundColor="white" borderWidth={1} w={40} h={40} padding={0} borderRadius={12}>
							<ChevronLeft color="black" />
						</Button>
						<Button onPress={handleNext} backgroundColor="white" borderWidth={1} w={40} h={40} padding={0} borderRadius={12}>
							<ChevronRight color="black" />
						</Button>
					</Box>
				)}
			</Box>
		</Box>
	);
};
interface SlideContent {
	image: any; // require("path") result
	title: string;
	subtitle?: string;
}

const Slide = ({ image, title, subtitle }: SlideContent) => {
	const isMobile = useIsMobile();
	const imageBackgroundStyleWeb = isMobile ? { flex: 1, width: "100%", minHeight: 200 } : { width: "100%", height: "100%" };
	const TextContent = (
		<Box flex={1}>
			<Box
				sx={{
					flex: 1,
					justifyContent: "center",
					paddingHorizontal: isMobile ? 24 : 48,
					paddingVertical: isMobile ? 32 : 0,
					gap: subtitle ? 8 : 0,
				}}
			>
				<Box py={64} gap={24} justifyContent="center" flex={1}>
					<Typography weight="bold" sx={{ fontSize: 60, lineHeight: "normal", color: "$basicblack" }}>
						{title}
					</Typography>
					{subtitle ? <Typography sx={{ color: "$basicblack" }}>{subtitle}</Typography> : null}
					<PageButton variant="primary" size="small" text="Book now" type="enquireForm" href="/services/servicing-and-maintenance" />
				</Box>
			</Box>
		</Box>
	);
	const ImageContent = (
		<Box flex={1} bg="$basicwhite">
			<ImageBackground source={image} resizeMode="cover" style={imageBackgroundStyleWeb as any}>
				<LinearGradient
					start={isMobile ? { x: 0, y: 1 } : { x: 0, y: 1 }}
					end={isMobile ? { x: 0, y: 0.8 } : { x: 1, y: 1 }}
					colors={["#E3F1FA", "transparent"]}
					style={
						isMobile
							? StyleSheet.absoluteFillObject
							: {
									position: "absolute",
									left: 0,
									top: 0,
									bottom: 0,
									width: "20%",
								}
					}
				></LinearGradient>
			</ImageBackground>
		</Box>
	);

	return (
		<Box flexDirection={isMobile ? "column" : "row"} borderRadius={12} overflow="hidden" minHeight={isMobile ? 400 : 578} bg="#E3F1FA">
			{isMobile ? (
				<>
					{ImageContent}
					{TextContent}
				</>
			) : (
				<>
					{TextContent}
					{ImageContent}
				</>
			)}
		</Box>
	);
};

const BannerImageSlider = ({ type = "equipment" }: { type: "products" | "equipment" }) => {
	const productSlideList: SlideContent[] = [
		{
			image: require("assets/images/cleaning_products banner.jpg"),
			title: "Products",
			subtitle:
				"Discover Abco’s comprehensive range of commercial cleaning and hygiene solutions, trusted across Australia. From eco-friendlier cleanings solutions to high-performance chemicals and durable floorcare solutions, our range is designed to meet the highest standards of cleanliness and efficiency. ",
		},
	];
	const equipmentSlideList: SlideContent[] = [
		{
			image: require("assets/abco-service-maintenance.jpg"),
			title: "Equipment",
			subtitle: "Machine Down? Need Fast Repairs or Diagnosis? Our expert team is ready to get you back up and running. Contact us now for urgent breakdown service or machine diagnostics!",
		},
		// {
		// 	image: require("assets/cover-values/own-it.png"),
		// 	title: "Own It – Excellence in Every Detail",
		// 	subtitle: "Taking responsibility to provide unmatched service.",
		// },
	];

	let slides = type === "products" ? productSlideList : equipmentSlideList;
	return (
		<Container>
			<Box marginVertical={12}>
				<Box position="relative">
					<InternalBannerSlider autoPlay autoPlayInterval={7000} showIndicators showArrows>
						{slides.map((slide, idx) => (
							<Slide key={idx} {...slide} />
						))}
					</InternalBannerSlider>
				</Box>
			</Box>
		</Container>
	);
};

export default BannerImageSlider;
